import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import LanguageSwitcher from "../i18n/LanguageSwitcher";
import UsageChart from "./UsageChart";
import { useUserUsage } from "../hooks/useUserUsage";

export const UserPanel = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { usageEvents, refreshUsageEvents } = useUserUsage();
  const [rangeDays, setRangeDays] = useState(30);

  useEffect(() => {
    const endAt = Math.floor(Date.now() / 1000);
    const startAt = endAt - rangeDays * 24 * 60 * 60;
    refreshUsageEvents(startAt, endAt);
  }, [rangeDays, refreshUsageEvents]);

  const profileLabel = useMemo(() => {
    if (!user) return "";
    return user.name || user.email;
  }, [user]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold">
          {t("user.panel_title", "Profile")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("user.panel_subtitle", "Manage your preferences and usage")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("user.account_title", "Account")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="text-sm text-muted-foreground">
                {t("user.name", "Name")}
              </div>
              <div className="text-sm font-medium">
                {profileLabel || t("user.unknown", "Unknown")}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">
                {t("user.email", "Email")}
              </div>
              <div className="text-sm font-medium">
                {user?.email || t("user.unknown", "Unknown")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user?.is_admin ? (
                <Badge variant="success">{t("user.admin_badge", "Admin")}</Badge>
              ) : (
                <Badge variant="outline">{t("user.user_badge", "User")}</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("user.preferences_title", "Preferences")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">
                    {t("user.language", "Language")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("user.language_hint", "Change your interface language")}
                  </div>
                </div>
                <LanguageSwitcher />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("user.usage_title", "Usage")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UsageChart
                prefix="user"
                events={usageEvents.data}
                isLoading={usageEvents.isLoading}
                rangeDays={rangeDays}
                onRangeDaysChange={setRangeDays}
              />
              {usageEvents.error && (
                <div className="text-xs text-destructive mt-2">
                  {usageEvents.error}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UserPanel;
